import { useState, useMemo } from "react";
import {
  Container,
  Title,
  Paper,
  Stack,
  Group,
  Text,
  Table,
  Badge,
  Button,
  FileInput,
  Select,
  LoadingOverlay,
  Alert,
  Progress,
  Collapse,
  ActionIcon,
  Loader,
} from "@mantine/core";
import {
  IconUpload,
  IconCheck,
  IconX,
  IconChevronDown,
  IconChevronUp,
  IconFileText,
  IconAlertCircle,
} from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  workListService,
  type WorkListExtractedCase,
  type ExpectedCase,
} from "../../services/workList.service";
import { usePractices } from "../../hooks/admin/usePractices";

const POLL_INTERVAL = 5000;

export default function WorkListsPage() {
  const queryClient = useQueryClient();

  // Upload state
  const [file, setFile] = useState<File | null>(null);
  const [practiceId, setPracticeId] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  // Preview state — which work list is being previewed for confirmation
  const [previewWorkListId, setPreviewWorkListId] = useState<string | null>(
    null
  );
  const [confirming, setConfirming] = useState(false);

  // Expanded row state
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedCases, setExpandedCases] = useState<ExpectedCase[]>([]);

  // Load practices
  const { data: practicesData = [] } = usePractices();
  const practices = practicesData
    .filter((p) => p.isActive)
    .map((p) => ({ value: p.id, label: p.name }));

  // Poll work lists — refetch every 5s while any are still "processing"
  const { data: workLists = [], isLoading } = useQuery({
    queryKey: ["workLists"],
    queryFn: () => workListService.getAll(),
    refetchInterval: (query) => {
      const data = query.state.data;
      const hasProcessing = data?.some((wl) => wl.status === "processing");
      return hasProcessing ? POLL_INTERVAL : false;
    },
  });

  // If the previewed work list finished extracting, pull its data
  const previewWorkList = useMemo(
    () => workLists.find((wl) => wl.id === previewWorkListId),
    [workLists, previewWorkListId]
  );
  const previewCases: WorkListExtractedCase[] =
    previewWorkList?.status === "extracted"
      ? previewWorkList.rawData?.extractedCases || []
      : [];
  const previewTotalRows =
    previewWorkList?.status === "extracted"
      ? previewWorkList.rawData?.totalRows || 0
      : 0;

  const handleUpload = async () => {
    if (!file || !practiceId) return;
    setUploading(true);
    try {
      const { workListId } = await workListService.upload(file, practiceId);
      setPreviewWorkListId(workListId);
      setFile(null);
      // Immediately refetch so the new "processing" row appears in the table
      queryClient.invalidateQueries({ queryKey: ["workLists"] });
      notifications.show({
        title: "Upload started",
        message: "Extracting data from work list PDF — this may take a moment",
        color: "blue",
      });
    } catch (error: any) {
      notifications.show({
        title: "Upload failed",
        message: error.message,
        color: "red",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!previewWorkListId || previewCases.length === 0) return;
    setConfirming(true);
    try {
      await workListService.confirmWorkList(previewWorkListId, previewCases);
      notifications.show({
        title: "Work list confirmed",
        message: `${previewCases.length} expected cases created`,
        color: "teal",
      });
      setPreviewWorkListId(null);
      setPracticeId(null);
      queryClient.invalidateQueries({ queryKey: ["workLists"] });
    } catch (error: any) {
      notifications.show({
        title: "Confirm failed",
        message: error.message,
        color: "red",
      });
    } finally {
      setConfirming(false);
    }
  };

  const handleCancelPreview = () => {
    setPreviewWorkListId(null);
  };

  const toggleExpanded = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedCases([]);
      return;
    }
    try {
      const cases = await workListService.getExpectedCases(id);
      setExpandedCases(cases);
      setExpandedId(id);
    } catch {
      // Expected cases load failed silently
    }
  };

  const statusColor = (status: string) => {
    switch (status) {
      case "completed":
        return "teal";
      case "extracted":
        return "green";
      case "processing":
        return "blue";
      case "error":
        return "red";
      default:
        return "gray";
    }
  };

  const statusLabel = (status: string) => {
    switch (status) {
      case "extracted":
        return "ready to confirm";
      default:
        return status;
    }
  };

  const expectedStatusColor = (status: string) => {
    switch (status) {
      case "matched":
        return "teal";
      case "expired":
        return "orange";
      case "cancelled":
        return "red";
      default:
        return "blue";
    }
  };

  // Show preview when extraction is done
  const showPreview =
    previewWorkListId && previewWorkList?.status === "extracted";
  const showProcessing =
    previewWorkListId && previewWorkList?.status === "processing";
  const showError = previewWorkListId && previewWorkList?.status === "error";

  return (
    <Container size="xl" py="md">
      <Stack gap="lg">
        <Group justify="space-between">
          <Title order={2}>Bay Area Endoscopy Work Lists</Title>
        </Group>

        {/* Upload Section */}
        <Paper shadow="xs" p="md" withBorder>
          <Stack gap="sm">
            <Title order={4}>Upload Work List PDF</Title>
            <Group align="end" gap="sm">
              <Select
                label="Practice"
                placeholder="Select practice"
                data={practices}
                value={practiceId}
                onChange={setPracticeId}
                searchable
                w={300}
              />
              <FileInput
                label="Work List PDF"
                placeholder="Choose file"
                accept="application/pdf"
                value={file}
                onChange={setFile}
                leftSection={<IconFileText size={16} />}
                w={300}
              />
              <Button
                onClick={handleUpload}
                loading={uploading}
                disabled={!file || !practiceId}
                leftSection={<IconUpload size={16} />}
              >
                Upload & Extract
              </Button>
            </Group>

            {/* Processing indicator */}
            {showProcessing && (
              <Group gap="xs" mt="xs">
                <Loader size="xs" />
                <Text size="sm" c="blue">
                  Extracting data from PDF — polling for results...
                </Text>
              </Group>
            )}

            {/* Error indicator */}
            {showError && (
              <Alert color="red" variant="light" mt="xs">
                Extraction failed:{" "}
                {previewWorkList?.errorMessage || "Unknown error"}
              </Alert>
            )}
          </Stack>
        </Paper>

        {/* Preview Section — shown when extraction completes */}
        {showPreview && previewCases.length > 0 && (
          <Paper shadow="xs" p="md" withBorder>
            <Stack gap="sm">
              <Group justify="space-between">
                <Title order={4}>
                  Extracted Cases: {previewCases.length} unique cases from{" "}
                  {previewTotalRows} rows
                </Title>
                <Group>
                  <Button
                    color="teal"
                    onClick={handleConfirm}
                    loading={confirming}
                    leftSection={<IconCheck size={16} />}
                  >
                    Confirm & Create Expected Cases
                  </Button>
                  <Button
                    variant="outline"
                    color="gray"
                    onClick={handleCancelPreview}
                    leftSection={<IconX size={16} />}
                  >
                    Cancel
                  </Button>
                </Group>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Accession</Table.Th>
                    <Table.Th>Patient</Table.Th>
                    <Table.Th>Specimens</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {previewCases.map((ec) => (
                    <Table.Tr key={ec.accession}>
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {ec.accession}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{ec.patient}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Stack gap={2}>
                          {ec.rows.map((row, i) => (
                            <Text key={i} size="xs" c="dimmed">
                              {row.siteLetter}: {row.site} ({row.stain})
                            </Text>
                          ))}
                        </Stack>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Stack>
          </Paper>
        )}

        {/* Work Lists Dashboard */}
        <Paper shadow="xs" p="md" withBorder pos="relative">
          <LoadingOverlay visible={isLoading} />
          <Stack gap="sm">
            <Title order={4}>Uploaded Work Lists</Title>

            {workLists.length === 0 && !isLoading ? (
              <Alert
                icon={<IconAlertCircle size={16} />}
                color="gray"
                variant="light"
              >
                No work lists uploaded yet. Upload a Bay Area Endoscopy work
                list PDF above.
              </Alert>
            ) : (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th />
                    <Table.Th>Upload Date</Table.Th>
                    <Table.Th>File Name</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>Cases</Table.Th>
                    <Table.Th>Matched</Table.Th>
                    <Table.Th>Pending</Table.Th>
                    <Table.Th>Progress</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {workLists.map((wl) => {
                    const pending = wl.caseCount - wl.matchedCount;
                    const progress =
                      wl.caseCount > 0
                        ? Math.round((wl.matchedCount / wl.caseCount) * 100)
                        : 0;
                    const isExpanded = expandedId === wl.id;

                    return (
                      <>
                        <Table.Tr key={wl.id}>
                          <Table.Td>
                            {wl.status === "completed" && (
                              <ActionIcon
                                variant="subtle"
                                size="sm"
                                onClick={() => toggleExpanded(wl.id)}
                              >
                                {isExpanded ? (
                                  <IconChevronUp size={14} />
                                ) : (
                                  <IconChevronDown size={14} />
                                )}
                              </ActionIcon>
                            )}
                            {wl.status === "processing" && <Loader size={14} />}
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">
                              {new Date(wl.createdAt).toLocaleDateString()}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" fw={500}>
                              {wl.fileName || "-"}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Badge
                              color={statusColor(wl.status)}
                              variant="light"
                              size="sm"
                            >
                              {statusLabel(wl.status)}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm">{wl.caseCount}</Text>
                          </Table.Td>
                          <Table.Td>
                            <Text size="sm" c="teal" fw={500}>
                              {wl.matchedCount}
                            </Text>
                          </Table.Td>
                          <Table.Td>
                            <Text
                              size="sm"
                              c={pending > 0 ? "orange" : "teal"}
                              fw={500}
                            >
                              {pending}
                            </Text>
                          </Table.Td>
                          <Table.Td w={120}>
                            <Progress
                              value={progress}
                              color={progress === 100 ? "teal" : "blue"}
                              size="sm"
                            />
                          </Table.Td>
                        </Table.Tr>

                        {isExpanded && (
                          <Table.Tr key={`${wl.id}-expanded`}>
                            <Table.Td colSpan={8}>
                              <Collapse in={isExpanded}>
                                <Paper p="sm" bg="gray.0" withBorder>
                                  {expandedCases.length === 0 ? (
                                    <Text size="sm" c="dimmed">
                                      No expected cases found.
                                    </Text>
                                  ) : (
                                    <Table>
                                      <Table.Thead>
                                        <Table.Tr>
                                          <Table.Th>Accession</Table.Th>
                                          <Table.Th>Patient</Table.Th>
                                          <Table.Th>Specimens</Table.Th>
                                          <Table.Th>Status</Table.Th>
                                        </Table.Tr>
                                      </Table.Thead>
                                      <Table.Tbody>
                                        {expandedCases.map((ec) => (
                                          <Table.Tr key={ec.id}>
                                            <Table.Td>
                                              <Text size="sm" fw={500}>
                                                {ec.externalCaseNumber}
                                              </Text>
                                            </Table.Td>
                                            <Table.Td>
                                              <Text size="sm">
                                                {ec.patientLastName}
                                                {ec.patientFirstName
                                                  ? `, ${ec.patientFirstName}`
                                                  : ""}
                                              </Text>
                                            </Table.Td>
                                            <Table.Td>
                                              <Stack gap={2}>
                                                {ec.specimensData?.map(
                                                  (row, i) => (
                                                    <Text
                                                      key={i}
                                                      size="xs"
                                                      c="dimmed"
                                                    >
                                                      {row.siteLetter}:{" "}
                                                      {row.site} ({row.stain})
                                                    </Text>
                                                  )
                                                )}
                                              </Stack>
                                            </Table.Td>
                                            <Table.Td>
                                              <Badge
                                                color={expectedStatusColor(
                                                  ec.status
                                                )}
                                                variant="light"
                                                size="sm"
                                              >
                                                {ec.status}
                                              </Badge>
                                            </Table.Td>
                                          </Table.Tr>
                                        ))}
                                      </Table.Tbody>
                                    </Table>
                                  )}
                                </Paper>
                              </Collapse>
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </>
                    );
                  })}
                </Table.Tbody>
              </Table>
            )}
          </Stack>
        </Paper>
      </Stack>
    </Container>
  );
}
